import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import type { WindowCatalogItem, WindowTechnology } from "../model/types";

import { useGetShadingSystemCatalog } from "../api/hooks";
import { applyWindowCatalogPrefill } from "../lib/windowCatalogFormMappers";
import { buildWindowConfigDrawerConfig } from "../lib/windowConfigDrawerFields";
import { WindowConfigFormState } from "../lib/windowConfigFieldDefinitions";
import { defaultWindowConfigFormState, mapTechnologyToFormState } from "../lib/windowConfigForm";
import {
  buildShadingSystemTypeSuggestions,
  getMatchingShadingCatalogItems,
  getShadingLocationDrawerOptions,
  hasShadingSelectionPrerequisites,
  isShadingSystemSelected,
  resolveShadingCatalogItemSelection,
  SHADING_LOCATION_NONE,
  SHADING_SYSTEM_TYPE_NONE,
} from "../lib/windowConfigOptions";
import { buildWindowConfigSavePayload } from "../lib/windowConfigPayload";
import { mapShadingCatalogItemToFormFields } from "../lib/windowShadingMappers";

type HeaderCatalogField = "frameType" | "windowTypeName";

type UseWindowConfigFormOptions = {
  catalog: WindowCatalogItem[];
  enabled: boolean;
  hasTechnology: boolean;
  initialTechnology?: null | WindowTechnology;
  isCatalogLoading: boolean;
  isLoading: boolean;
  recordKey?: string;
};

const isHeaderCatalogField = (field: string): field is HeaderCatalogField =>
  field === "windowTypeName" || field === "frameType";

const clearShadingSystemFields = (state: WindowConfigFormState): { changed: boolean; state: WindowConfigFormState } => {
  const { gValueDiffuseWindowShading, gValueDirectWindowShading, maxWindAverage, maxWindExtreme, shadingSystemType } =
    state;

  const isnonEmpty = [
    gValueDiffuseWindowShading,
    gValueDirectWindowShading,
    maxWindAverage,
    maxWindExtreme,
    isShadingSystemSelected(shadingSystemType) ? shadingSystemType : "",
  ].some((value) => value !== "");
  if (!isnonEmpty) return { changed: false, state };

  return {
    changed: true,
    state: {
      ...state,
      gValueDiffuseWindowShading: "",
      gValueDirectWindowShading: "",
      maxWindAverage: "",
      maxWindExtreme: "",
      shadingSystemType: SHADING_SYSTEM_TYPE_NONE,
    },
  };
};

export const useWindowConfigForm = ({
  catalog,
  enabled,
  hasTechnology,
  initialTechnology,
  isCatalogLoading,
  isLoading,
  recordKey,
}: UseWindowConfigFormOptions) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<WindowConfigFormState>(defaultWindowConfigFormState());
  const headerFieldBaselineRef = useRef<Partial<Record<HeaderCatalogField, string>>>({});

  const hasWindowTypeAndFrameType = Boolean(formState.windowTypeName?.trim() && formState.frameType?.trim());
  const canSelectShadingType = hasShadingSelectionPrerequisites(formState);

  const { data: shadingCatalog = [] } = useGetShadingSystemCatalog(undefined, enabled && hasWindowTypeAndFrameType);

  const noneLocationLabel = String(t("TechnicalData.windows.fields.shadingLocationNone"));

  const shadingCatalogMatch = useMemo(() => {
    if (!canSelectShadingType) {
      return {
        filterContext: {
          canFilterByFrameType: false,
          canFilterByLocation: false,
          canFilterByWindowType: false,
          showFrameTypeInLabels: false,
          showLocationInLabels: false,
          showWindowTypeInLabels: false,
        },
        items: [],
      };
    }

    return getMatchingShadingCatalogItems(shadingCatalog, catalog, formState);
  }, [canSelectShadingType, catalog, formState, shadingCatalog]);

  const shadingSystemTypeSuggestions = useMemo(
    () => buildShadingSystemTypeSuggestions(shadingCatalogMatch, catalog, noneLocationLabel),
    [catalog, noneLocationLabel, shadingCatalogMatch]
  );

  const shadingLocationOptions = useMemo(() => {
    if (!hasWindowTypeAndFrameType) {
      return { displayOptions: [], options: [] };
    }

    return getShadingLocationDrawerOptions(shadingCatalog, catalog, formState, noneLocationLabel);
  }, [catalog, formState, hasWindowTypeAndFrameType, noneLocationLabel, shadingCatalog]);

  useEffect(() => {
    if (!enabled || isLoading || isCatalogLoading) return;

    if (!initialTechnology) {
      if (!hasTechnology) setFormState(defaultWindowConfigFormState());
      return;
    }

    setFormState(mapTechnologyToFormState(initialTechnology, catalog));
  }, [catalog, enabled, hasTechnology, initialTechnology, isCatalogLoading, isLoading, recordKey]);

  const resolveShadingSelection = useCallback(
    (current: WindowConfigFormState, selection: string) => {
      const matchResult = getMatchingShadingCatalogItems(shadingCatalog, catalog, current);
      const suggestions = buildShadingSystemTypeSuggestions(matchResult, catalog, noneLocationLabel);

      return resolveShadingCatalogItemSelection(selection, suggestions, matchResult, catalog, noneLocationLabel);
    },
    [catalog, noneLocationLabel, shadingCatalog]
  );

  const commitHeaderCatalogField = useCallback(
    (current: WindowConfigFormState, field: HeaderCatalogField, nextValue: string) => {
      const baseline = headerFieldBaselineRef.current[field] ?? current[field];
      if (nextValue === baseline) {
        return applyWindowCatalogPrefill({ ...current, [field]: nextValue }, catalog);
      }

      const { changed, state } = clearShadingSystemFields({ ...current, [field]: nextValue });
      if (changed) toast.warn(t("TechnicalData.windows.fields.shadingSystemDataCleared"));

      headerFieldBaselineRef.current[field] = nextValue;
      return applyWindowCatalogPrefill(
        changed ? { ...state, shadingLocation: SHADING_LOCATION_NONE } : state,
        catalog
      );
    },
    [catalog, t]
  );

  const updateField = useCallback(
    (field: keyof WindowConfigFormState | string, value: unknown) => {
      if (
        typeof field === "string" &&
        field.endsWith("__focus") &&
        isHeaderCatalogField(field.replace("__focus", ""))
      ) {
        const headerField = field.replace("__focus", "") as HeaderCatalogField;
        headerFieldBaselineRef.current[headerField] = String(value ?? "");
        return;
      }

      if (
        typeof field === "string" &&
        field.endsWith("__commit") &&
        isHeaderCatalogField(field.replace("__commit", ""))
      ) {
        const headerField = field.replace("__commit", "") as HeaderCatalogField;
        setFormState((current) => commitHeaderCatalogField(current, headerField, String(value ?? "")));
        return;
      }

      setFormState((current) => {
        if (field === "windowTypeName") {
          const nextWindowTypeName = String(value ?? "");
          if (nextWindowTypeName === current.windowTypeName) return current;

          return applyWindowCatalogPrefill({ ...current, windowTypeName: nextWindowTypeName }, catalog);
        }

        if (field === "frameType") {
          const nextFrameType = String(value ?? "");
          if (nextFrameType === current.frameType) return current;

          return applyWindowCatalogPrefill({ ...current, frameType: nextFrameType }, catalog);
        }

        if (field === "shadingLocation") {
          const nextShadingLocation = value === undefined || value === null ? "" : String(value);
          if (nextShadingLocation === current.shadingLocation) return current;

          const { changed, state } = clearShadingSystemFields({ ...current, shadingLocation: nextShadingLocation });
          if (changed) toast.warn(t("TechnicalData.windows.fields.shadingSystemDataCleared"));

          return state;
        }

        if (field !== "shadingSystemType") {
          if (current[field as keyof WindowConfigFormState] === value) return current;
          return { ...current, [field as keyof WindowConfigFormState]: value };
        }

        const nextShadingSystemType =
          value === undefined || value === null || value === ""
            ? SHADING_SYSTEM_TYPE_NONE
            : String(value);
        if (nextShadingSystemType === current.shadingSystemType) return current;

        const shadingItem = resolveShadingSelection(current, nextShadingSystemType);

        if (!shadingItem) {
          const suggestions = buildShadingSystemTypeSuggestions(
            getMatchingShadingCatalogItems(shadingCatalog, catalog, current),
            catalog,
            noneLocationLabel
          );

          if (
            isShadingSystemSelected(nextShadingSystemType) &&
            !suggestions.options.includes(nextShadingSystemType)
          ) {
            return current;
          }

          if (!isShadingSystemSelected(nextShadingSystemType)) {
            return {
              ...current,
              gValueDiffuseWindowShading: "",
              gValueDirectWindowShading: "",
              maxWindAverage: "",
              maxWindExtreme: "",
              shadingSystemType: SHADING_SYSTEM_TYPE_NONE,
            };
          }

          return { ...current, shadingSystemType: nextShadingSystemType };
        }

        return {
          ...current,
          ...mapShadingCatalogItemToFormFields(shadingItem),
          shadingLocation: current.shadingLocation,
        };
      });
    },
    [catalog, commitHeaderCatalogField, noneLocationLabel, resolveShadingSelection, shadingCatalog, t]
  );

  const { fields, tabs } = useMemo(
    () =>
      buildWindowConfigDrawerConfig({
        canSelectShadingType,
        catalog,
        formState,
        hasWindowTypeAndFrameType,
        noneLocationLabel,
        onFieldChange: updateField,
        shadingCatalogMatch,
        shadingLocationOptions,
        shadingSystemTypeSuggestions,
        t,
      }),
    [
      canSelectShadingType,
      catalog,
      formState,
      hasWindowTypeAndFrameType,
      noneLocationLabel,
      shadingCatalogMatch,
      shadingLocationOptions,
      shadingSystemTypeSuggestions,
      t,
      updateField,
    ]
  );

  return {
    fields,
    formState,
    mapPayload: () =>
      buildWindowConfigSavePayload({
        catalog,
        formState,
        shadingCatalog,
      }),
    tabs,
  };
};
